import React from 'react';
import styled from 'styled-components';
import { Star, Quote } from 'lucide-react';
import { Container, Section, Badge } from '../styles/GlobalStyles';

const TestimonialsSection = styled(Section)`
  background: var(--gray-900);
  color: var(--gray-0);
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, var(--gray-900) 0%, var(--gray-800) 100%);
    opacity: 0.9;
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: var(--space-16);
  position: relative;
  z-index: 1;
  
  .badge {
    margin-bottom: var(--space-4);
    background: rgba(255, 255, 255, 0.1);
    color: var(--gray-0);
    border: 1px solid rgba(255, 255, 255, 0.2);
  }
  
  h2 {
    font-size: var(--text-5xl);
    color: var(--gray-0);
    margin-bottom: var(--space-6);
    font-weight: 800;
    
    @media (max-width: 768px) {
      font-size: var(--text-4xl);
    }
  }
  
  .subtitle {
    font-size: var(--text-xl);
    color: var(--gray-300);
    max-width: 600px;
    margin: 0 auto;
  }
`;

const TestimonialGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: var(--space-8);
  position: relative;
  z-index: 1;
`;

const TestimonialCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: var(--radius-2xl);
  padding: var(--space-8);
  transition: all var(--transition-normal);
  
  &:hover {
    transform: translateY(-8px);
    border-color: rgba(255, 255, 255, 0.2);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  }
  
  .quote-icon {
    color: var(--primary-400);
    margin-bottom: var(--space-4);
  }
  
  .content {
    font-size: var(--text-lg);
    line-height: 1.6;
    color: var(--gray-200);
    margin-bottom: var(--space-6);
    font-style: italic;
  }
  
  .author {
    display: flex;
    align-items: center;
    gap: var(--space-4);
    
    .info {
      .name {
        font-weight: 600;
        color: var(--gray-0);
        margin-bottom: var(--space-1);
      }
      
      .role {
        font-size: var(--text-sm);
        color: var(--gray-400);
      }
    }
  }
  
  .rating {
    display: flex;
    gap: var(--space-1);
    margin-bottom: var(--space-4);
    
    .star {
      color: var(--yellow-400);
    }
  }
`;

const AuthorAvatar = styled.div`
  width: 48px;
  height: 48px;
  background: var(--gradient-primary);
  border-radius: var(--radius-full);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--gray-0);
  font-weight: 700;
  font-size: var(--text-lg);
`;

const testimonials = [
  {
    content: "Working with Willovate has been fantastic. They delivered our restaurant management system on time and exceeded our expectations. The team is professional and truly understands software development.",
    author: "Rajesh Kumar",
    role: "Restaurant Owner",
    rating: 5,
    initials: "RK"
  },
  {
    content: "Impressed with their technical expertise and attention to detail. The custom web application they built for us is exactly what we needed. Highly recommend for any software development project.",
    author: "Priya Sharma",
    role: "Business Owner",
    rating: 5,
    initials: "PS"
  },
  {
    content: "Willovate stands out for their clear communication and quality work. They took our ideas and turned them into a professional software solution. Great experience working with them.",
    author: "Michael Chen",
    role: "Startup Founder",
    rating: 5,
    initials: "MC"
  }
];

const Testimonials = () => {
  return (
    <TestimonialsSection>
      <Container>
        <SectionHeader>
          <Badge className="badge">
            <Star size={12} />
            Client Testimonials
          </Badge>
          <h2>What Our Clients Say</h2>
          <p className="subtitle">
            Don't just take our word for it - hear from the businesses 
            we've helped succeed with our software solutions.
          </p>
        </SectionHeader>

        <TestimonialGrid>
          {testimonials.map((testimonial, index) => (
            <TestimonialCard key={index}>
              <Quote size={32} className="quote-icon" />
              
              <div className="rating">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} size={16} className="star" fill="currentColor" />
                ))}
              </div>
              
              <p className="content">"{testimonial.content}"</p>
              
              <div className="author">
                <AuthorAvatar>
                  {testimonial.initials}
                </AuthorAvatar>
                <div className="info">
                  <div className="name">{testimonial.author}</div>
                  <div className="role">{testimonial.role}</div>
                </div>
              </div>
            </TestimonialCard>
          ))}
        </TestimonialGrid>
      </Container>
    </TestimonialsSection>
  );
};

export default Testimonials;
